/**
 * proFeaturesBindings.js — Привязка ВСЕХ функций viewport.js к UI
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * Заменяет заглушки (showToast-only) на реальные вызовы через callAPI().
 * Инжектирует кнопки для функций, у которых нет HTML-элементов.
 *
 * Инициализировать из main.js ПОСЛЕ featureBindings (перезаписывает placeholder'ы).
 */

import { callAPI } from '../core/viewerAPI.js';
import { on as busOn, EVENTS } from '../core/eventBus.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';
import { cornerstone } from '../core/init.js';
import { datasetManager } from '../managers/DatasetManager.js';
import { memoryGuardian } from '../managers/MemoryGuardian.js';
import { getAllSeries, getSeriesImageIds } from '../loaders/loaderManager.js'; // [7.0.52/53] 4D-авто-инициализация + выбор CT/PET серий

// Ленивая загрузка AI модуля (ONNX Runtime тяжёлый — не грузим при старте)
let _aiModule = null;
async function _ai() {
  if (_aiModule) return _aiModule;
  _aiModule = await import('../ai/aiModule.js');
  return _aiModule;
}
import { getRenderingEngine, getViewportIds } from '../core/viewportState.js';
import { popCameraState, getCameraHistoryLength, disablePositionSync, disableHUProbe, disableAdaptiveQuality, stopCine, getCineState, setCineSpeed, loadAnnotations, deleteAnnotation, clearAnnotations, exportAnnotationsJSON, importAnnotationsJSON, enableAnnotationAutoSave, removeVolumeOverlay, setOverlayOpacity, addHangingProtocol, listHangingProtocols, disableVoiceControl, addVoiceCommand, listVoiceCommands, computeRectROIStats, stopSessionRecording, replaySession, exportSessionJSON, disableGestures, enableAllGestures, stopSmartCompare, disableKeyboardShortcuts, addKeyboardShortcut, listKeyboardShortcuts, applyWLPreset, autoWindowLevel, goToBookmark, removeBookmark, getBookmarks, navigateBookmarks, enableMagnifier, disableMagnifier, showRulerGrid, removeRulerGrid, hideInfoOverlay, getSlabState, cycleSlabProjection, disableMPRSync, getVolumeClipping, addBookmark, startCine, showInfoOverlay, setSlabProjection, pushCameraState, startSessionRecording, init4DViewer, enablePositionSync, enableMPRSync, enableAdaptiveQuality, jumpToPathology } from '../core/viewport.js';
import { setCardiacPhase, nextCardiacPhase, prevCardiacPhase, start4DPlayback, stop4DPlayback, get4DState, detectCardiacPhases } from '../core/viewport4D.js';
import { downloadScreenshot, screenshotAll, downloadDICOMCapture } from '../core/viewportScreenshot.js';
import { animatePresetTransition } from '../core/viewportEffects.js';
// [11.0.2] Окно справки открывается через общего распорядителя окон.
import { openModal } from './modalManager.js';

// [FIX] AbortController для очистки всех addEventListener при destroy
let _ac = new AbortController();


const $ = (id) => document.getElementById(id);

// ══════════════════════════════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════════════════════════════

function _getActiveVpId() {
  const ids = getViewportIds();
  return ids?.[0] || null;
}

/**
 * Заменяет все обработчики кнопки (clone + replace)
 */
function _rebind(id, handler) {
  const el = $(id);
  if (!el) return null;
  const newEl = el.cloneNode(true);
  el.parentNode.replaceChild(newEl, el);
  newEl.addEventListener('click', handler, { signal: _ac.signal });
  return newEl;
}

// ══════════════════════════════════════════════════════════════
//  BIND: Закладки (Bookmarks) — заменяем заглушки
// ══════════════════════════════════════════════════════════════

function bindBookmarks() {
  _rebind('btn-bookmark-add', () => {
    const vpId = _getActiveVpId();
    if (!vpId) { showToast('⚠️ Нет активного окно', 'warning'); return; }
    const bm = addBookmark(vpId, '', true);
    if (bm) showToast(`⭐ Закладка #${getBookmarks(vpId)?.length || 1} добавлена`, 'success', 1200);
    else showToast('⚠️ Не удалось добавить закладку', 'warning');
  });

  _rebind('btn-bookmark-prev', () => {
    const vpId = _getActiveVpId();
    if (vpId) navigateBookmarks(vpId, 'prev');
  });

  _rebind('btn-bookmark-next', () => {
    const vpId = _getActiveVpId();
    if (vpId) navigateBookmarks(vpId, 'next');
  });
}

// ══════════════════════════════════════════════════════════════
//  BIND: CINE (Авто-прокрутка) — заменяем старые на viewport.js
// ══════════════════════════════════════════════════════════════

// [10.19.32] Четыре сироты панели наборов удалены: btn-ds-load-4d-cine,
// btn-ds-load-wholebody, btn-ds-play-cine, btn-ds-stop-cine — кнопок нет в
// разметке (панель наборов сведена к слиянию PET-CT). Живое 4D — кнопки
// btn-pro-4d-* и вкладка фаз (cardiacPhases, cardiac4d_test).

function bindCine() {
  // [10.19.32] Вкладочная пара кино (btn-cine-play/stop-tab) удалена: кнопок
  // нет с 10.19.19/26 — «кино в одном разделе». Живое кино: нижняя панель
  // (cine-play здесь же ниже) и кнопка тулбара btn-cine-toolbar.
  // Sidebar CINE play/stop


  // [10.19.32] Блок cine-fps-tab удалён вместе с элементом.

  // Bottom cine bar play button
  _rebind('cine-play', () => {
    const state = getCineState();
    if (state?.playing) {
      stopCine();
    } else {
      const vpId = _getActiveVpId();
      if (vpId) {
        const fpsEl = $('cine-fps');
        const fps = fpsEl ? parseInt(fpsEl.value) || 12 : 12;
        startCine(vpId, { fps, direction: 'forward', loop: !!$('cine-loop')?.checked });
      }
    }
  });
}

// ══════════════════════════════════════════════════════════════
//  BIND: DICOM Secondary Capture — заменяем заглушку
// ══════════════════════════════════════════════════════════════

// [10.19.32] bindDICOMSC удалён целиком: его единственная кнопка btn-export-sc
// давно вырезана из разметки. Живой DICOM SC — btn-pro-dicom-capture ниже
// (тот же downloadDICOMCapture).

// ══════════════════════════════════════════════════════════════
//  BIND: Инжектированные кнопки → viewport.js функции
// ══════════════════════════════════════════════════════════════

function bindProButtons() {
  const on = (id, fn) => { const el = $(id); if (el) el.addEventListener('click', fn, { signal: _ac.signal }); };

  // ── Лупа / Сетка / Инфо ──
  let _magnifierOn = false;
  on('btn-pro-magnifier', () => {
    const vpId = _getActiveVpId();
    if (!vpId) return;
    _magnifierOn = !_magnifierOn;
    if (_magnifierOn) { enableMagnifier(vpId); showToast('🔍 Лупа ВКЛ', 'info', 800); }
    else { disableMagnifier(vpId); showToast('🔍 Лупа ВЫКЛ', 'info', 800); }
    $('btn-pro-magnifier')?.classList.toggle('active', _magnifierOn);
  });

  let _rulerOn = false;
  on('btn-pro-ruler-grid', () => {
    const vpId = _getActiveVpId();
    if (!vpId) return;
    _rulerOn = !_rulerOn;
    if (_rulerOn) { showRulerGrid(vpId); showToast('📏 Сетка ВКЛ', 'info', 800); }
    else { removeRulerGrid(vpId); showToast('📏 Сетка ВЫКЛ', 'info', 800); }
    $('btn-pro-ruler-grid')?.classList.toggle('active', _rulerOn);
  });

  let _infoOn = false;
  on('btn-pro-info-overlay', () => {
    const vpId = _getActiveVpId();
    if (!vpId) return;
    _infoOn = !_infoOn;
    if (_infoOn) { showInfoOverlay(vpId); showToast('ℹ️ Инфо-оверлей ВКЛ', 'info', 800); }
    else { hideInfoOverlay(vpId); showToast('ℹ️ Инфо-оверлей ВЫКЛ', 'info', 800); }
    $('btn-pro-info-overlay')?.classList.toggle('active', _infoOn);
  });

  // ── Slab Projection ──
  on('btn-pro-slab-mip', () => {
    const vpId = _getActiveVpId();
    if (vpId) { setSlabProjection(vpId, 'mip', 10); showToast('🎬 MIP 10mm', 'info', 1000); }
  });
  on('btn-pro-slab-minip', () => {
    const vpId = _getActiveVpId();
    if (vpId) { setSlabProjection(vpId, 'minip', 10); showToast('🎬 MinIP 10mm', 'info', 1000); }
  });
  on('btn-pro-slab-avg', () => {
    const vpId = _getActiveVpId();
    if (vpId) { setSlabProjection(vpId, 'avg', 10); showToast('🎬 Среднее 10mm', 'info', 1000); }
  });
  on('btn-pro-slab-cycle', () => {
    const vpId = _getActiveVpId();
    if (vpId) {
      cycleSlabProjection(vpId, 10);
      const state = getSlabState();
      showToast(`🔄 Слэб: ${state?.mode?.toUpperCase() || 'off'}`, 'info', 800);
    }
  });

  // ── Screenshots ──
  on('btn-pro-screenshot-all', async () => {
    try {
      // [7.0.52 FIX] screenshotAll возвращает data-URL коллажа (строку) или null, НЕ массив.
      // Раньше: format:'image/png' → внутри 'image/image/png', а blobs.length = длина строки
      // (тост «287342 скриншотов»), и коллаж НИКУДА не сохранялся.
      const dataUrl = await screenshotAll({ format: 'png' });
      if (!dataUrl) { showToast('⚠️ Нет вьюпортов для скриншота', 'warning'); return; }
      const a = document.createElement('a');
      a.href = dataUrl; a.download = `screenshot_all_${Date.now()}.png`; a.click();
      showToast('📷 Коллаж-скриншот сохранён', 'success', 1500);
    } catch (e) { showToast('⚠️ ' + e.message, 'error'); }
  });
  on('btn-pro-dicom-capture', () => {
    const vpId = _getActiveVpId();
    if (vpId) {
      try {
        downloadDICOMCapture(vpId, `SC_${Date.now()}.dcm`);
        showToast('💾 DICOM SC сохранён', 'success', 1500);
      } catch (e) { showToast('⚠️ ' + e.message, 'error'); }
    }
  });

  // ── Camera push / pop ──
  on('btn-pro-cam-push', () => {
    const vpId = _getActiveVpId();
    if (vpId) { pushCameraState(vpId); showToast(`📌 Камера сохранена (стек: ${getCameraHistoryLength(vpId)})`, 'success', 1000); }
  });
  on('btn-pro-cam-pop', () => {
    const vpId = _getActiveVpId();
    if (vpId) {
      const len = getCameraHistoryLength(vpId);
      if (len > 0) { popCameraState(vpId); showToast(`↩ Камера восстановлена (стек: ${len - 1})`, 'info', 1000); }
      else showToast('↩ Стек пуст', 'info', 800);
    }
  });

  // ── Smart Compare ──
  let _comparing = false;
  on('btn-pro-smart-compare', () => {
    if (_comparing) {
      stopSmartCompare();
      _comparing = false;
      $('btn-pro-smart-compare')?.classList.remove('active');
      showToast('⚖️ Сравнение остановлено', 'info', 800);
    } else {
      showToast('⚖️ Загрузите 2 исследования для сравнения\n(функция активируется автоматически)', 'info', 3000);
    }
  });

  // ── Session Recording ──
  let _sessionData = null;
  on('btn-pro-session-start', () => {
    startSessionRecording({ source: 'ui' });
    $('btn-pro-session-start')?.classList.add('active');
    showToast('🔴 Запись сессии начата', 'info', 1500);
  });
  on('btn-pro-session-stop', () => {
    _sessionData = stopSessionRecording();
    $('btn-pro-session-start')?.classList.remove('active');
    if (_sessionData) showToast(`⏹ Записано: ${_sessionData.events?.length || 0} событий, ${((_sessionData.duration || 0) / 1000).toFixed(0)}с`, 'success', 2000);
    else showToast('⏹ Нет записи', 'info', 800);
  });
  on('btn-pro-session-export', () => {
    if (!_sessionData) { showToast('⚠️ Сначала запишите сессию', 'warning'); return; }
    const json = exportSessionJSON(_sessionData);
    if (json) {
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `session_${Date.now()}.json`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      showToast('📤 Сессия экспортирована', 'success', 1500);
    }
  });

  // ── Keyboard shortcuts list ──
  on('btn-pro-kb-list', () => {
    // [11.0.2] Раньше здесь список из тридцати строк вываливался ВСПЛЫВАЮЩЕЙ
    // ПЛАШКОЙ на 8 секунд. Прочитать её нельзя, прокрутить нельзя, скопировать
    // нельзя — та самая беда, из-за которой в 10.19.29 проверку моделей увели
    // из плашки в окно, а в 10.19.44 туда же ушла диагностика.
    //
    // Теперь кнопка открывает окно «Горячие клавиши» — то самое, где таблица
    // клавиш лежит целиком и куда при роспуске панели переехала группа
    // «Горячие клавиши / Шпаргалка». Двух списков в двух видах больше нет:
    // кнопка и окно ведут в одно место.
    // [11.0.2] Через openModal, а не classList.add('active'): окно ведёт
    // ModalManager — ловушка фокуса, Escape, клик по подложке. Самодельное
    // открытие запрещено сторожем (uiwiring_test, «окна открываются только
    // через ModalManager») — и правильно: самоделка открывает ничто, молча.
    try { openModal('modal-help'); }
    catch (e) { log.debug('[Клавиши] окно справки:', e.message); }
  });

  // ── Auto W/L ──
  on('btn-pro-auto-wl', () => {
    const vpId = _getActiveVpId();
    if (vpId) { autoWindowLevel(vpId); showToast('🎯 Авто W/L', 'info', 1000); }
  });

  // ── 4D ──
  on('btn-pro-4d-prev', () => prevCardiacPhase());
  on('btn-pro-4d-next', () => nextCardiacPhase());
  let _4dPlaying = false;
  on('btn-pro-4d-play', () => {
    if (_4dPlaying) { stop4DPlayback(); _4dPlaying = false; $('btn-pro-4d-play')?.classList.remove('active'); showToast('⏹ 4D Стоп', 'info', 800); return; }
    // [7.0.52 FIX] Раньше кнопка ЛГАЛА: init4DViewer нигде не вызывался → _4dState=null →
    // start4DPlayback молча no-op, но кнопка вставала в active и тост «▶ 4D Play». Теперь честно:
    // если 4D не инициализирован — пытаемся инициализировать из многофазных серий, а если их
    // нет (нет тегов фазы) — прямо говорим, а не притворяемся.
    if (!get4DState()) {
      const phases = detectCardiacPhases(getAllSeries?.() || []);
      const hasPhaseInfo = phases.length >= 2 && phases.some(p => p.phasePercent > 0);
      if (!hasPhaseInfo) { showToast('⚠️ 4D: нужна многофазная кардио-серия (теги фазы 0018,1060 / 0020,9241)', 'warning', 4000); return; }
      init4DViewer(phases, getViewportIds());
      if (!get4DState()) { showToast('⚠️ 4D: не удалось инициализировать', 'warning', 3000); return; }
      showToast(`🫀 4D: ${phases.length} фаз обнаружено`, 'info', 1500);
    }
    start4DPlayback({ fps: 8, loop: true });
    _4dPlaying = true; $('btn-pro-4d-play')?.classList.add('active'); showToast('▶ 4D Пуск', 'info', 800);
  });
  on('btn-pro-4d-stop', () => {
    stop4DPlayback();
    _4dPlaying = false;
    $('btn-pro-4d-play')?.classList.remove('active');
    showToast('⏹ 4D Стоп', 'info', 800);
  });

  // ── Pos sync / MPR sync toggles ──
  let _posSyncOn = false;
  on('btn-pro-pos-sync', () => {
    _posSyncOn = !_posSyncOn;
    if (_posSyncOn) {
      const ids = getViewportIds();
      enablePositionSync(ids);
      showToast('🔗 Позиционная синхр. ВКЛ', 'info', 1000);
    } else {
      disablePositionSync();
      showToast('🔗 Позиционная синхр. ВЫКЛ', 'info', 1000);
    }
    $('btn-pro-pos-sync')?.classList.toggle('active', _posSyncOn);
  });

  let _mprSyncOn = false;
  on('btn-pro-mpr-sync', () => {
    _mprSyncOn = !_mprSyncOn;
    if (_mprSyncOn) { enableMPRSync(); showToast('🔗 MPR W/L синхр. ВКЛ', 'info', 1000); }
    else { disableMPRSync(); showToast('🔗 MPR W/L синхр. ВЫКЛ', 'info', 1000); }
    $('btn-pro-mpr-sync')?.classList.toggle('active', _mprSyncOn);
  });

  // ── ROI Stats ──
  on('btn-pro-roi-circle', () => {
    showToast('⭕ Нарисуйте круговую область инструментом, статистика отобразится автоматически', 'info', 3000);
  });
  on('btn-pro-roi-rect', () => {
    showToast('▬ Нарисуйте прямоугольную область инструментом, статистика отобразится автоматически', 'info', 3000);
  });

  // ── Adaptive quality ──
  on('btn-pro-adaptive-on', () => {
    const vpId = _getActiveVpId();
    if (vpId) { enableAdaptiveQuality(vpId); showToast('⚡ Адаптивное качество ВКЛ', 'info', 1000); }
    $('btn-pro-adaptive-on')?.classList.add('active');
    $('btn-pro-adaptive-off')?.classList.remove('active');
  });
  on('btn-pro-adaptive-off', () => {
    const vpId = _getActiveVpId();
    if (vpId) { disableAdaptiveQuality(vpId); showToast('⚡ Адаптивное качество ВЫКЛ', 'info', 1000); }
    $('btn-pro-adaptive-on')?.classList.remove('active');
  });

  // ── AI pathology jump ──
  on('btn-pro-jump-pathology', async () => {
    try {
      await jumpToPathology();
      showToast('🎯 Переход к патологии', 'info', 1000);
    } catch (e) {
      showToast('⚠️ ' + (e.message || 'Патология не найдена'), 'warning');
    }
  });
}

// ══════════════════════════════════════════════════════════════
//  BIND: EventBus listeners для информирования пользователя
// ══════════════════════════════════════════════════════════════

function bindEventListeners() {
  // HU Probe → обновляем statusbar (desktop + mobile)
  busOn('hu:probe', (data) => {
    const val = data?.hu != null ? `HU: ${Math.round(data.hu)}` : 'HU: —';
    const el = $('hu-value-display');
    if (el) el.textContent = val;
    const elM = $('hu-value-display-m');
    if (elM) elM.textContent = val;
  });

  // CINE events → обновляем UI кнопок
  busOn('cine:start', () => {
    $('btn-cine-play-tab')?.classList.add('active');
    $('cine-play')?.classList.add('active');
  });
  busOn('cine:stop', () => {
    $('btn-cine-play-tab')?.classList.remove('active');
    $('cine-play')?.classList.remove('active');
  });

  // Keyboard shortcuts ready
  busOn('keyboard:shortcut', (data) => {
    log.debug(`[KB] ${data?.code}: ${data?.label}`);
  });

  // 4D phase changes
  busOn('phase:changed', (data) => {
    const el = $('4d-phase-display');
    if (el) el.textContent = `Фаза: ${(data?.phase ?? 0) + 1}/${data?.totalPhases || '?'}`;
  });

  // Auto W/L applied
  busOn('auto:wl:applied', (data) => {
    if (data?.preset) showToast(`🎯 Авто W/L: ${data.preset}`, 'info', 1200);
  });
}

// ══════════════════════════════════════════════════════════════
//  BIND: Enterprise Dataset Manager (4D Cine, PET-CT, Whole Body)
// ══════════════════════════════════════════════════════════════

/** Текущий активный датасет (для UI-кнопок play/stop/destroy) */
let _activeDatasetId = null;

function bindDatasetManager() {
  const on = (id, fn) => { const el = $(id); if (el) el.addEventListener('click', fn, { signal: _ac.signal }); };

  // ── Прогресс-бар и статус (общие для всех типов) ──
  const progressBar = $('ds-progress-bar');
  const progressText = $('ds-progress-text');
  const statusText = $('ds-status');

  datasetManager.addEventListener('progress', (e) => {
    const { id, percent } = e.detail;
    if (progressBar) { progressBar.style.width = `${percent}%`; progressBar.textContent = `${percent}%`; }
    if (progressText) progressText.textContent = `${percent}%`;
  }, { signal: _ac.signal });

  datasetManager.addEventListener('loaded', (e) => {
    if (progressBar) { progressBar.style.width = '100%'; progressBar.textContent = '100%'; }
    if (statusText) statusText.textContent = `✓ Загружен: ${e.detail.id}`;
    showToast(`✅ Датасет загружен: ${e.detail.id}`, 'success', 2000);
    log.info(`[DS] Fully loaded: ${e.detail.id}`);
  }, { signal: _ac.signal });

  datasetManager.addEventListener('destroyed', (e) => {
    if (statusText) statusText.textContent = '';
    if (progressBar) { progressBar.style.width = '0%'; progressBar.textContent = ''; }
    showToast(`🗑️ Датасет удалён: ${e.detail.id}`, 'info', 1500);
    if (_activeDatasetId === e.detail.id) _activeDatasetId = null;
  }, { signal: _ac.signal });

  datasetManager.addEventListener('cine-frame', (e) => {
    const el = $('ds-cine-frame-display');
    if (el) el.textContent = `Кадр: ${e.detail.frame + 1}`;
  }, { signal: _ac.signal });

  datasetManager.addEventListener('cine-play', (e) => {
    // [10.19.32] Подсветка btn-ds-play-cine убрана вместе с кнопкой-сиротой.
    $('btn-ds-stop-cine')?.classList.toggle('active', !e.detail.playing);
  }, { signal: _ac.signal });

  // ── 4D CINE ──



  // FPS slider для DatasetManager CINE
  const dsFpsSlider = $('ds-cine-fps');
  if (dsFpsSlider) {
    const dsFpsVal = $('ds-cine-fps-val');
    dsFpsSlider.addEventListener('input', () => {
      const fps = parseInt(dsFpsSlider.value) || 25;
      if (dsFpsVal) dsFpsVal.textContent = fps;
      if (_activeDatasetId) {
        const vpId = _getActiveVpId();
        const re = getRenderingEngine();
        const vp = vpId ? re?.getViewport(vpId) : null;
        datasetManager.setCineFPS(_activeDatasetId, fps, vp);
      }
    }, { signal: _ac.signal });
  }

  // Timepoint slider (ручной скроллинг по фазам)
  const dsTimepointSlider = $('ds-cine-timepoint');
  if (dsTimepointSlider) {
    dsTimepointSlider.addEventListener('input', () => {
      if (!_activeDatasetId) return;
      const t = parseInt(dsTimepointSlider.value) || 0;
      const vpId = _getActiveVpId();
      const re = getRenderingEngine();
      const vp = vpId ? re?.getViewport(vpId) : null;
      datasetManager.setCineTimepoint(_activeDatasetId, t, vp);
    }, { signal: _ac.signal });
  }

  // ── [7.0.53] Выбор CT/PET серий → window vars, которые нужны loadPETCTFusion ──
  const _esc = (s) => String(s ?? '').replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c]));
  function _refreshFusionSeries(silent) {
    const ctSel = $('ds-petct-ct-series'), petSel = $('ds-petct-pet-series');
    if (!ctSel || !petSel) return;
    const series = getAllSeries?.() || [];
    const opts = '<option value="">—</option>' + series.map((s, i) => {
      const uid = s.seriesUID || s.seriesInstanceUID || '';
      const nm = s.seriesDescription || s.modality || `Серия ${i + 1}`;
      return `<option value="${_esc(uid)}">${_esc(nm)}${s.modality ? ' [' + _esc(s.modality) + ']' : ''}</option>`;
    }).join('');
    const ctv = ctSel.value, petv = petSel.value;         // сохранить выбор при перерисовке
    ctSel.innerHTML = opts; petSel.innerHTML = opts;
    ctSel.value = ctv; petSel.value = petv;
    if (!silent) showToast(`↻ Загруженных серий: ${series.length}`, 'info', 1200);
  }
  function _setFusionSeriesVars() {
    const ctUID = $('ds-petct-ct-series')?.value || '';
    const petUID = $('ds-petct-pet-series')?.value || '';
    window._mikhmelPETCT_ctIds = ctUID ? getSeriesImageIds(ctUID) : null;
    window._mikhmelPETCT_petIds = petUID ? getSeriesImageIds(petUID) : null;
  }
  on('btn-ds-refresh-series', () => _refreshFusionSeries(false));
  $('ds-petct-ct-series')?.addEventListener('change', _setFusionSeriesVars, { signal: _ac.signal });
  $('ds-petct-pet-series')?.addEventListener('change', _setFusionSeriesVars, { signal: _ac.signal });
  _refreshFusionSeries(true); // при старте серий ещё нет — заполнится по кнопке ↻

  // ── PET-CT Fusion ──
  on('btn-ds-load-petct', async () => {
    // PET-CT требует два набора imageIds — CT и PET
    // В реальном UI они задаются через выбор серий в sidebar
    const ctIds = window._mikhmelPETCT_ctIds;
    const petIds = window._mikhmelPETCT_petIds;

    if (!ctIds?.length || !petIds?.length) {
      showToast('⚠️ Выберите CT и PET серии для слияния\n(Загрузите оба набора через боковую панель)', 'warning', 4000);
      return;
    }

    try {
      showToast('⏳ Готовлю слияние PET-CT…', 'info', 3000);
      const ds = await datasetManager.loadPETCTFusion(ctIds, petIds);
      _activeDatasetId = ds.id;
      if (statusText) statusText.textContent = `Слияние PET-CT: ${ctIds.length} CT + ${petIds.length} PET`;
      showToast('🔥 Слияние PET-CT готово', 'success', 2000);
    } catch (e) {
      if (e.name !== 'AbortError') showToast('⚠️ Ошибка PET-CT: ' + e.message, 'error');
    }
  });

  on('btn-ds-apply-fusion', async () => {
    if (!_activeDatasetId) { showToast('⚠️ Сначала загрузите PET-CT', 'warning'); return; }
    const re = getRenderingEngine();
    const vpIds = getViewportIds();
    if (!re || !vpIds.length) return;

    try {
      await datasetManager.applyFusion(_activeDatasetId, re, vpIds);
      showToast('🔥 Слияние применено ко всем окнам', 'success', 1500);
    } catch (e) {
      showToast('⚠️ ' + e.message, 'error');
    }
  });

  // Fusion opacity slider
  const fusionOpacity = $('ds-fusion-opacity');
  if (fusionOpacity) {
    fusionOpacity.addEventListener('input', () => {
      if (!_activeDatasetId) return;
      const opacity = parseFloat(fusionOpacity.value) || 0.5;
      const re = getRenderingEngine();
      const vpIds = getViewportIds();
      datasetManager.updateFusionProperties(_activeDatasetId, re, vpIds, { opacity });
    }, { signal: _ac.signal });
  }

  // Fusion colormap select
  const fusionColormap = $('ds-fusion-colormap');
  if (fusionColormap) {
    fusionColormap.addEventListener('change', () => {
      if (!_activeDatasetId) return;
      const colormap = fusionColormap.value || 'hot';
      const re = getRenderingEngine();
      const vpIds = getViewportIds();
      datasetManager.updateFusionProperties(_activeDatasetId, re, vpIds, { colormap });
    }, { signal: _ac.signal });
  }

  // ── Whole Body MRI ──

  // Station navigation buttons
  const stationBtns = document.querySelectorAll('[data-wb-station]');
  stationBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      if (!_activeDatasetId) return;
      const idx = parseInt(btn.dataset.wbStation) || 0;
      const station = datasetManager.navigateToStation(_activeDatasetId, idx);
      if (station) showToast(`📍 Станция: ${station.label}`, 'info', 1000);
    }, { signal: _ac.signal });
  });

  // ── Общие кнопки управления ──
  on('btn-ds-destroy', () => {
    if (!_activeDatasetId) { showToast('⚠️ Нет активного датасета', 'warning'); return; }
    datasetManager.destroyDataset(_activeDatasetId);
  });

  on('btn-ds-clear-all', () => {
    datasetManager.clearAll();
    showToast('🗑️ Все датасеты удалены', 'info', 1500);
  });

  // ── Memory Guardian UI ──
  on('btn-memory-status', () => {
    if (!performance.memory) {
      showToast('ℹ️ API памяти недоступен в этом браузере', 'info', 2000);
      return;
    }
    const mem = performance.memory;
    const used = (mem.usedJSHeapSize / 1024 / 1024).toFixed(0);
    const limit = (mem.jsHeapSizeLimit / 1024 / 1024).toFixed(0);
    const ratio = ((mem.usedJSHeapSize / mem.jsHeapSizeLimit) * 100).toFixed(1);
    const datasets = datasetManager.getAllDatasets();
    showToast(`🧠 RAM: ${used} / ${limit} MB (${ratio}%)\n` +
      `📦 Датасетов: ${datasets.length}\n` +
      `🛡️ Страж: ${memoryGuardian.hasMemoryAPI ? 'Active' : 'Passive'}`,
      'info', 5000
    );
  });

  log.debug('[ProFeatures] DatasetManager & MemoryGuardian wired to UI');
}

// ══════════════════════════════════════════════════════════════
//  HELPER: Извлечение данных тома из активного viewport
// ══════════════════════════════════════════════════════════════

/**
 * Получает scalarData, dims, spacing из загруженного тома.
 * Паттерн идентичен computeCalciumScore в viewport.js.
 * @returns {{ scalarData, dims, spacing, origin, direction, volumeId } | null}
 */
function _getActiveVolumeData() {
  const re = getRenderingEngine();
  const diag = []; // diagnostic log
  if (!re) { diag.push('RE=null'); log.warn('[AI-DIAG]', diag.join(' | ')); return null; }

  let volume = null;
  let volumeId = null;
  const cache = cornerstone.cache;
  diag.push(`cache=${!!cache}`, `cache.getVolume=${typeof cache?.getVolume}`);

  // 1. Ищем том через actors в viewport'ах
  const vpIds = getViewportIds();
  diag.push(`vpIds=[${vpIds.join(',')}]`);

  for (const vpId of vpIds) {
    const vp = re.getViewport(vpId);
    if (!vp) { diag.push(`vp(${vpId})=null`); continue; }
    diag.push(`vp(${vpId}).type=${vp.type}`);
    
    const actors = vp.getActors?.();
    diag.push(`actors=${actors?.length || 0}`);
    
    if (actors?.length) {
      const a0 = actors[0];
      const uid = a0.uid || a0.referencedId || a0.referenceId || a0.volumeId;
      diag.push(`actor0.keys=[${Object.keys(a0).join(',')}]`, `uid=${uid}`);
      
      if (uid) {
        // Пробуем несколько вариантов получения тома из кэша
        volume = cache?.getVolume?.(uid);
        if (!volume) volume = cache?.getVolume?.(`cornerstoneStreamingImageVolume:${uid}`);
        if (!volume) {
          // Пробуем без префикса если uid уже содержит его
          const cleanId = uid.replace('cornerstoneStreamingImageVolume:', '');
          volume = cache?.getVolume?.(`cornerstoneStreamingImageVolume:${cleanId}`);
        }
        volumeId = uid;
        diag.push(`volume=${!!volume}`);
        if (volume) break;
      }
    }

    // Для любого viewport — пробуем getImageData
    try {
      const imageData = vp.getImageData?.();
      if (imageData) {
        const scalarData = imageData.getPointData?.()?.getScalars?.()?.getData?.();
        const dims = imageData.getDimensions?.();
        const spacing = imageData.getSpacing?.();
        diag.push(`imageData: dims=${dims}, scalars=${scalarData?.length || 0}`);
        if (scalarData?.length && dims) {
          log.info('[AI-DIAG] Found via getImageData:', diag.join(' | '));
          // [10.19.19] origin и direction тоже наружу: без них точку экрана нельзя
          // перевести в индексы объёма (наращивание области), а origin у
          // медицинских серий — это сотни миллиметров со знаком, не ноль.
          const origin = imageData.getOrigin?.();
          const direction = imageData.getDirection?.();
          return {
            scalarData, dims, spacing: spacing || [1, 1, 1],
            origin: origin ? Array.from(origin) : [0, 0, 0],
            direction: direction && direction.length === 9 ? Array.from(direction) : null,
            volumeId: volumeId || 'imageData',
          };
        }
      }
    } catch (e) { diag.push(`imageData.err=${e.message}`); }
  }

  // 2. Fallback: перебираем весь кэш
  if (!volume && cache) {
    try {
      const volCache = cache._volumeCache || cache.getVolumes?.();
      diag.push(`volCache=${!!volCache}, type=${volCache?.constructor?.name}`);
      if (volCache) {
        const entries = volCache instanceof Map ? [...volCache.entries()] :
                        typeof volCache[Symbol.iterator] === 'function' ? [...volCache] :
                        Array.isArray(volCache) ? volCache.map((v,i) => [i, v]) :
                        Object.entries(volCache);
        diag.push(`volCache.entries=${entries.length}`);
        for (const [vid, entry] of entries) {
          const v = entry?.volume || entry;
          if (v?.dimensions) {
            volume = v;
            volumeId = vid;
            diag.push(`fallback.found=${vid}, dims=${v.dimensions}`);
            break;
          }
        }
      }
    } catch (e) { diag.push(`fallback.err=${e.message}`); }
  }

  log.warn('[AI-DIAG]', diag.join(' | '));
  if (!volume) return null;

  // 3. Извлекаем скалярные данные
  let scalarData = null;
  try { scalarData = volume.voxelManager?.getCompleteScalarDataArray?.(); } catch (e) { log.debug("[proFeaturesBindings]", e.message); }
  if (!scalarData && volume.imageData) {
    try { scalarData = volume.imageData.getPointData?.()?.getScalars?.()?.getData?.(); } catch (e) { log.debug("[proFeaturesBindings]", e.message); }
  }
  if (!scalarData) { try { scalarData = volume.getScalarData?.(); } catch (e) { log.debug("[proFeaturesBindings]", e.message); } }
  if (!scalarData) { log.warn('[AI-DIAG] volume found but no scalarData'); return null; }

  const dims = volume.dimensions || volume.imageData?.getDimensions?.() || [512, 512, 1];
  const spacing = volume.spacing || volume.imageData?.getSpacing?.() || [1, 1, 1];

  // origin/direction нужны для перевода координат экрана в индексы объёма.
  let origin = null, direction = null;
  try {
    origin = volume.origin || volume.imageData?.getOrigin?.();
    direction = volume.direction || volume.imageData?.getDirection?.();
  } catch (e) { /* геометрии нет — вернём безопасные значения */ }
  return {
    scalarData, dims, spacing, volumeId,
    origin: origin ? Array.from(origin) : [0, 0, 0],
    direction: direction && direction.length === 9 ? Array.from(direction) : null,
  };
}

// ══════════════════════════════════════════════════════════════
//  BIND: AI Module (Все алгоритмы → UI кнопки)
// ══════════════════════════════════════════════════════════════

function bindAI() {
  const on = (id, fn) => { const el = $(id); if (el) el.addEventListener('click', fn, { signal: _ac.signal }); };

  // ── Общий guard: требуется загруженный том ──
  function _requireVolume(label) {
    const data = _getActiveVolumeData();
    if (!data) {
      showToast(`⚠️ ${label}: том не найден. Подробности в консоли (F12 → консоль → ИИ-диагностика)`, 'warning', 5000);
      return null;
    }
    log.info(`[AI] ${label}: volume OK, dims=${data.dims}, scalars=${data.scalarData.length}`);
    return data;
  }

  /**
   * [10.19.19] ШКАЛА ЗНАЧЕНИЙ: та ли она, по которой заданы пороги.
   *
   * ПОВОД — замер приёмки. Кнопки «Органы», «Мозг», «Сердце» отвечали
   * «🏥 Органы (0)», «Кровоизлияние: 0 мл», «Камеры не обнаружены». Формально
   * кнопка работает, ответ есть. Но врач из такого ответа может сделать только
   * один вывод — кнопка сломана. А причина в другом: пороги сегментации заданы в
   * единицах Хаунсфилда (воздух около −1000), и если в объёме лежат «сырые»
   * значения без пересчёта (например 20…627), под порог не попадает НИЧЕГО.
   *
   * Поэтому перед пороговой сегментацией шкала проверяется, и при несовпадении
   * говорится числами: вот диапазон объёма, вот чего ждали. Это отказ с
   * названной причиной, а не ноль без объяснения.
   */
  function _шкалаHU(vol) {
    const a = vol.scalarData;
    let мин = Infinity, макс = -Infinity;
    const шаг = Math.max(1, Math.floor(a.length / 20000));
    for (let i = 0; i < a.length; i += шаг) { const v = a[i]; if (v < мин) мин = v; if (v > макс) макс = v; }
    // Воздух в единицах Хаунсфилда — около −1000. Если минимум объёма выше −300,
    // это либо не КТ, либо значения не пересчитаны в HU.
    return { мин, макс, похожеНаHU: мин < -300 };
  }

  function _предупредитьОШкале(vol, что) {
    const ш = _шкалаHU(vol);
    if (ш.похожеНаHU) return false;
    showToast(`${что}: значения объёма ${Math.round(ш.мин)}…${Math.round(ш.макс)} — это не шкала Хаунсфилда `
      + '(воздух должен быть около −1000). Пороговая сегментация по таким значениям ничего не найдёт: '
      + 'нужна серия КТ с пересчётом в HU (RescaleSlope/Intercept) — на МРТ и на «сырых» значениях этот путь не работает.',
      'warning', 11000);
    return true;
  }

  // ── Выбор затравки для наращивания области ────────────────────────────────
  //
  // Механизм простой нарочно: следующий щелчок по окну со срезами берётся как
  // точка затравки, координата переводится в индексы объёма, дальше работает
  // алгоритм из clinicalInlineCore. Отмена — повторным нажатием кнопки или Esc:
  // режим, из которого нельзя выйти, — это ловушка, а не удобство.
  let _ждёмЗатравку = null;

  function _отменитьЗатравку(сообщение) {
    if (!_ждёмЗатравку) return;
    const { снять } = _ждёмЗатравку;
    _ждёмЗатравку = null;
    try { снять(); } catch (e) { log.debug('[Наращивание]', e.message); }
    const кн = document.getElementById('btn-ai-region-grow');
    if (кн) kнСброс(кн);
    if (сообщение) showToast(сообщение, 'info', 2500);
  }

  function kнСброс(кн) { кн.classList.remove('active'); кн.textContent = кн.dataset.подпись || кн.textContent; }

  function _включитьВыборЗатравки(vol) {
    const кн = document.getElementById('btn-ai-region-grow');
    if (кн && !кн.dataset.подпись) кн.dataset.подпись = кн.textContent;
    if (кн) { кн.classList.add('active'); кн.textContent = 'Укажите точку…'; }

    const обработчик = async (ev) => {
      const эл = ev.currentTarget;
      try {
        const re = getRenderingEngine?.() || window.MikhmelViewer?.getRenderingEngine?.();
        const vpId = эл.getAttribute('data-viewport-uid') || эл.id?.replace('viewport-', '');
        const vp = vpId ? re?.getViewport(vpId) : null;
        if (!vp || String(vp.type || '').toLowerCase().includes('3d')) {
          showToast('Точку затравки нужно указать в окне со срезами, не в объёмном', 'warning', 4000);
          return;
        }
        const прям = эл.getBoundingClientRect();
        const canvasPos = [ev.clientX - прям.left, ev.clientY - прям.top];
        const мир = vp.canvasToWorld?.(canvasPos);
        if (!мир) { showToast('Окно не перевело точку в координаты пациента', 'warning', 4000); return; }

        // Мир → индексы объёма: тем же способом, что и карта глубины.
        const { мирВИндексы, вИндексы } = await import('../rendering/depthMath.js');
        const преобр = мирВИндексы(vol.origin || [0, 0, 0], vol.spacing, vol.direction || null);
        if (!преобр) { showToast('У тома нулевой шаг — перевод координат невозможен', 'error', 5000); return; }
        const и = вИндексы(преобр, мир).map((v) => Math.round(v));
        const [nx, ny, nz] = vol.dims;
        if (и[0] < 0 || и[1] < 0 || и[2] < 0 || и[0] >= nx || и[1] >= ny || и[2] >= nz) {
          showToast(`Точка вне объёма (индексы ${и.join(', ')} при размерах ${vol.dims.join('×')})`, 'warning', 5000);
          return;
        }

        _отменитьЗатравку(null);
        showToast('Наращивание области…', 'info', 1500);
        const ядро = await import('./clinicalInlineCore.js');
        const итог = ядро.regionGrow(vol.scalarData, vol.dims, vol.spacing, и, 100);
        // Имена полей — по ядру: count, seedHU, volMl, mask. Раньше в этом месте
        // я написал voxels/volumeMl «по памяти», и счётчик показал бы ноль при
        // исправном расчёте — ровно та ошибка, что уже была с totalVoxels выше.
        const мл = итог?.volMl;
        if (!итог || !(итог.count > 0)) {
          showToast('Область не выросла: в окне ±100 единиц от затравки нет связанных вокселей', 'warning', 6000);
          return;
        }
        showToast(`Область: ${итог.count.toLocaleString('ru-RU')} вокселей`
          + (мл != null ? `, ${мл} мл` : '')
          + `\nЗатравка: индексы ${и.join(', ')}, значение ${Math.round(итог.seedHU)}`
          + (итог.обрезано ? '\nДостигнут потолок 5 000 000 вокселей — область больше.' : '')
          + '\nЭто заливка по окну ±100 единиц от затравки, а не нейросеть.', 'success', 9000);

        // Маску показываем там, где она может быть показана сейчас, — в
        // кинематографическом слое. Обещать «маску на срезах» не буду: наложения
        // сегментации на 2D-окна в программе нет, и делать вид, что есть, нельзя.
        try {
          const cr = window.MikhmelViewer?._getCinematicRenderer?.();
          if (итог.mask && cr?.setAIMask && cr.isEnabled?.()) {
            cr.setAIMask(итог.mask, vol.dims, [0.2, 1, 0.4], 0.35);
            showToast('Маска показана в кинематографическом слое', 'info', 4000);
          }
        } catch (e) { log.debug('[Наращивание/маска]', e.message); }
      } catch (e) {
        showToast('Наращивание области не удалось: ' + e.message, 'error', 6000);
        log.warn('[Наращивание]', e);
      }
    };

    const окна = Array.from(document.querySelectorAll('#viewport-container [id^="viewport-"]'));
    окна.forEach((эл) => эл.addEventListener('click', обработчик, { once: true }));
    const поEsc = (e) => { if (e.key === 'Escape') _отменитьЗатравку('Выбор точки отменён'); };
    document.addEventListener('keydown', поEsc);
    _ждёмЗатравку = {
      снять: () => {
        окна.forEach((эл) => эл.removeEventListener('click', обработчик));
        document.removeEventListener('keydown', поEsc);
      },
    };
    showToast('Укажите точку внутри интересующей области в окне со срезами.\nОтмена — Esc или повторное нажатие кнопки.', 'info', 6000);
  }

  // ── 1. CALCIUM SCORING (Agatston) ──
  on('btn-ai-calcium', async () => {
    const vol = _requireVolume('Calcium Scoring');
    if (!vol) return;
    try {
      showToast('⏳ Кальциевый счёт (Agatston)...', 'info', 2000);
      const ai = await _ai();
      const result = await ai.calciumScoring(
        vol.scalarData, vol.dims[0], vol.dims[1], vol.dims[2], vol.spacing
      );
      const risk = result.risk || 'Unknown';
      const score = result.totalScore ?? 0;
      const vulnCount = result.lesions?.filter(l => l.isVulnerable).length || 0;
      showToast(`Agatston Балл: ${Math.round(score)}\n` +
        `Риск: ${risk}\n` +
        `Очагов: ${result.lesionCount || 0}\n` +
        `Объём: ${result.volumeMm3 || 0} мм³\n` +
        `Уязвимых бляшек: ${vulnCount}`,
        score > 400 ? 'error' : score > 100 ? 'warning' : 'success', 8000
      );
      log.info(`[AI] Calcium Score: ${Math.round(score)} (${risk}), lesions: ${result.lesionCount}, vuln: ${vulnCount}`);
    } catch (e) { showToast('⚠️ Кальций: ' + e.message, 'error'); }
  });

  // [10.19.19] Обработчик кнопки «Камеры сердца» (btn-ai-cardiac) УДАЛЁН как дубликат.
  //
  // ПОВОД — замер приёмки: на один щелчок приходило по три-четыре плашки, и
  // расчёт шёл дважды. Кнопка несёт и id, и class="ai-run-btn" с data-model,
  // поэтому её слушали ДВА обработчика: этот и общий роутер моделей в
  // featureBindings. Остался роутер: у него полоса выполнения, единый разбор
  // моделей и проверка шкалы значений. Индекс атрофии, который печатал только
  // этот обработчик, перенесён туда же — чтобы не потерялся.

  // ── 3. LUNG NODULE DETECTION ──
  // [11.0.2] Обработчик кнопки «Узлы лёгких» (btn-ai-lung-nodules) УДАЛЁН как
  // дубликат — по образцу 10.19.19, где так же поступили с «Органами»,
  // «Мозгом» и «Сердцем». Кнопка несёт и id, и class="ai-run-btn" с
  // data-model, поэтому её слушали ДВА обработчика: этот и общий роутер
  // моделей в featureBindings. Один щелчок — два полных обхода объёма.
  //
  // Сверено поимённо, что роутер печатает ВСЁ, что печатал этот, и больше:
  //   • диаметр — есть у обоих;
  //   • Lung-RADS — только у роутера. Здешний код печатал `n.category`, а
  //     detectLungNodules такого поля не возвращает вовсе (aiModule.js:956 —
  //     там `lungRADS`). То есть в каждой строке стояло «undefined»;
  //   • плотность узла (meanHU) — только у роутера;
  //   • ПОРОГ УВЕРЕННОСТИ. Роутер читает ползунок `#nodule-thresh`
  //     (featureBindings.js:422) и передаёт его в расчёт. Здешний обработчик
  //     ползунок игнорировал и всегда считал по умолчанию. При двух
  //     работающих обработчиках врач получал два ответа на один щелчок:
  //     один по своему порогу, другой мимо него.

  // ── 4. ADVANCED LUNG ANALYSIS (Emphysema + Pneumonia) ──
  on('btn-ai-lung-analysis', async () => {
    const vol = _requireVolume('Lung Analysis');
    if (!vol) return;
    try {
      showToast('⏳ Расширенный анализ лёгких...', 'info', 4000);
      const ai = await _ai();
      // Сначала сегментируем органы для получения маски лёгких
      const organs = await ai.segmentOrgansByHU(
        vol.scalarData, vol.dims[0], vol.dims[1], vol.dims[2], vol.spacing
      );
      const lungMask = organs.lungs?.mask;
      if (!lungMask) { showToast('⚠️ Лёгкие не сегментированы', 'warning'); return; }

      const result = await ai.advancedLungAnalysis(
        vol.scalarData, lungMask, vol.dims[0], vol.dims[1], vol.dims[2], vol.spacing
      );
      let msg = '🫁 Анализ лёгких:\n';
      if (result.emphysema) msg += `  Эмфизема: ${result.emphysema.percentAffected?.toFixed(1)}%\n`;
      if (result.pneumonia) msg += `  GGO/Пневмония: ${result.pneumonia.percentAffected?.toFixed(1)}%\n`;
      if (result.vesselFraction != null) msg += `  Сосуды: ${(result.vesselFraction * 100).toFixed(1)}%`;
      showToast(msg, 'info', 8000);
    } catch (e) { showToast('⚠️ Анализ лёгких: ' + e.message, 'error'); }
  });

  // [10.19.19] Обработчик кнопки «Органы» (btn-ai-organs) УДАЛЁН как дубликат.
  //
  // ПОВОД — замер приёмки: на один щелчок приходило по три-четыре плашки, и
  // расчёт шёл дважды. Кнопка несёт и id, и class="ai-run-btn" с data-model,
  // поэтому её слушали ДВА обработчика: этот и общий роутер моделей в
  // featureBindings. Остался роутер: у него полоса выполнения, единый разбор
  // моделей и проверка шкалы значений. Индекс атрофии, который печатал только
  // этот обработчик, перенесён туда же — чтобы не потерялся.

  // [10.19.19] Обработчик кнопки «Мозг» (btn-ai-brain) УДАЛЁН как дубликат.
  //
  // ПОВОД — замер приёмки: на один щелчок приходило по три-четыре плашки, и
  // расчёт шёл дважды. Кнопка несёт и id, и class="ai-run-btn" с data-model,
  // поэтому её слушали ДВА обработчика: этот и общий роутер моделей в
  // featureBindings. Остался роутер: у него полоса выполнения, единый разбор
  // моделей и проверка шкалы значений. Индекс атрофии, который печатал только
  // этот обработчик, перенесён туда же — чтобы не потерялся.

  // ── 7. STROKE CLASSIFICATION ──
  // [11.0.2] Обработчик кнопки «Инсульт» (btn-ai-stroke) УДАЛЁН как дубликат —
  // та же порода, что «Узлы лёгких» выше и тройка из 10.19.19.
  //
  // Здесь дубль был ещё и НЕИСПРАВЕН. Он печатал три поля — `result.type`,
  // `result.volumeMl`, `result.side`, — а classifyStroke не возвращает ни
  // одного из них (aiModule.js:1088 отдаёт classification, confidence,
  // leftMeanHU, rightMeanHU, asymmetryHU, highDensityLeft, highDensityRight,
  // note). Значит на экран всегда шло «Тип: N/A», а объём и сторона не
  // печатались никогда. Врач получал две плашки об одном расчёте, и одна из
  // них была пустой.
  //
  // Роутер (featureBindings.js:441) даёт настоящий ответ: заключение с
  // уверенностью в процентах, плотности обоих полушарий и их асимметрию, и —
  // единственный — заполняет строку `#stroke-result` под самой карточкой,
  // где ответ остаётся на виду, а не тает через 12 секунд.

  // ── 8. OPPORTUNISTIC SCREENING (Longevity) ──
  on('btn-ai-screening', async () => {
    const vol = _requireVolume('Screening');
    if (!vol) return;
    try {
      showToast('⏳ Оппортунистический скрининг...', 'info', 4000);
      const ai = await _ai();
      const [w, h, d] = vol.dims;

      const cirrhosis = await ai.cirrhosisRiskIndex(vol.scalarData, w, h, d, vol.spacing);
      const bodyComp = ai.bodyCompositionAnalysis(vol.scalarData, w, h, d, vol.spacing);
      const aneurysm = ai.aortaAneurysmScreening(vol.scalarData, w, h, d, vol.spacing);
      const incidental = ai.incidentalFindingsScreening(vol.scalarData, w, h, d);

      let msg = '🔬 Скрининг:\n';
      if (cirrhosis.liverSpleen != null) msg += `  Печень/Селез. = ${cirrhosis.liverSpleen.toFixed(2)}${cirrhosis.risk ? ' ⚠️' + cirrhosis.risk : ''}\n`;
      if (bodyComp.smi != null) msg += `  SMI: ${bodyComp.smi.toFixed(1)} см²/м²\n`;
      if (aneurysm.maxDiameterMm != null) msg += `  Аорта: ${aneurysm.maxDiameterMm.toFixed(1)} мм${aneurysm.aneurysm ? ' ⚠️' : ''}\n`;
      if (incidental.findings?.length) msg += `  Находки: ${incidental.findings.length}`;
      else msg += '  Инцидентальных находок нет ✅';

      showToast(msg, incidental.findings?.length ? 'warning' : 'success', 10000);
    } catch (e) { showToast('⚠️ Скрининг: ' + e.message, 'error'); }
  });

  // ── 9. SAM — Segment Anything (включение режима) ──
  let _samActive = false;
  on('btn-ai-sam', async () => {
    _samActive = !_samActive;
    $('btn-ai-sam')?.classList.toggle('active', _samActive);
    if (_samActive) {
      try {
        const ai = await _ai();
        await ai.warmupModels();
        showToast('🪄 SAM активирован — кликните на область интереса', 'success', 3000);
      } catch (e) {
        _samActive = false;
        $('btn-ai-sam')?.classList.remove('active');
        showToast('⚠️ SAM: ' + e.message, 'error');
      }
    } else {
      try { (await _ai()).clearSamCache(); } catch (e) { log.debug("[proFeaturesBindings]", e.message); }
      showToast('🪄 SAM выключен', 'info', 1000);
    }
  });

  // ── 10. AUTO CONTOUR (универсальный порог) ──
  on('btn-ai-auto-contour', async () => {
    const vol = _requireVolume('Auto Contour');
    if (!vol) return;
    if (_предупредитьОШкале(vol, 'Автоконтур')) return;
    try {
      showToast('⏳ Автоконтурирование...', 'info', 2000);
      const ai = await _ai();
      const result = await ai.autoContourByThreshold(
        vol.scalarData, vol.dims[0], vol.dims[1], vol.dims[2], vol.spacing
      );
      // Поле называется totalVoxels; voxelCount ядро не возвращало никогда, поэтому
      // счётчик вокселей всегда показывал 0 — при исправном контуре.
      showToast(`✏️ Контур: ${result.totalVoxels || 0} вокселей, ${result.volumeMl?.toFixed(1) || 0} мл`, 'success', 3000);
    } catch (e) { showToast('⚠️ Обводка: ' + e.message, 'error'); }
  });

  // ── 11. НАРАЩИВАНИЕ ОБЛАСТИ (region growing) ──
  //
  // ПОВОД, слова Андрея Вячеславовича про раздел «Сегментация»: «нужно проверить,
  // все ли кнопки наделены функциями… если есть кнопки-пустышки, то их нужно либо
  // наделить правильными функциями, либо удалить».
  //
  // Эта была пустышкой в самом обидном смысле: она печатала «кликните на область
  // интереса (Ctrl+Клик для запуска)» — и обработчика такого клика в программе НЕ
  // БЫЛО ни одного. То есть кнопка выдавала инструкцию к несуществующему
  // действию. При этом сам алгоритм написан и работает
  // (ui/clinicalInlineCore.js, regionGrow: заливка по окну ±HU от затравки).
  // Не хватало только связи между ними — её и добавляю.
  on('btn-ai-region-grow', () => {
    const vol = _requireVolume('Наращивание области');
    if (!vol) return;
    if (_ждёмЗатравку) { _отменитьЗатравку('Выбор точки отменён'); return; }
    _включитьВыборЗатравки(vol);
  });

  // ── 12. 3D MESH EXPORT (STL) ──
  on('btn-ai-export-stl', async () => {
    const vol = _requireVolume('STL Export');
    if (!vol) return;
    try {
      showToast('⏳ Генерация 3D сетку...', 'info', 3000);
      const ai = await _ai();
      // Сегментируем органы, берём маску для mesh
      const organs = await ai.segmentOrgansByHU(
        vol.scalarData, vol.dims[0], vol.dims[1], vol.dims[2], vol.spacing
      );
      // Ищем первый непустой орган
      const organName = Object.keys(organs).find(k => organs[k]?.mask);
      if (!organName) { showToast('⚠️ Нет сегментированных органов для сетку', 'warning'); return; }

      const mesh = ai.extractMesh(
        organs[organName].mask, vol.dims[0], vol.dims[1], vol.dims[2], vol.spacing
      );
      if (mesh?.blob) {
        const url = URL.createObjectURL(mesh.blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${organName}_mesh.stl`; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        showToast(`📦 STL: ${organName} (${mesh.triangleCount || '?'} треугольников)`, 'success', 3000);
      }
    } catch (e) { showToast('⚠️ STL: ' + e.message, 'error'); }
  });

  // ── 13. DETECT BODY PART (auto-detect modality region) ──
  on('btn-ai-detect-body', async () => {
    const vol = _requireVolume('Body Part');
    if (!vol) return;
    try {
      const ai = await _ai();
      const result = ai.detectBodyPart(vol.scalarData, vol.dims[0], vol.dims[1], vol.dims[2]);
      showToast(`🏷️ Область: ${result.bodyPart || 'Unknown'} (${(result.confidence * 100).toFixed(0)}%)`, 'info', 3000);
    } catch (e) { showToast('⚠️ ' + e.message, 'error'); }
  });

  // ── 14. RELEASE MODELS (освобождение GPU памяти) ──
  on('btn-ai-release', async () => {
    try {
      const ai = await _ai();
      await ai.releaseModels();
      showToast('🧹 AI модели выгружены из памяти', 'success', 2000);
    } catch (e) { showToast('⚠️ ' + e.message, 'error'); }
  });

  log.debug('[ProFeatures] AI Module (14 functions) wired to UI');
}

// ══════════════════════════════════════════════════════════════
//  PUBLIC: initProFeaturesBindings
// ══════════════════════════════════════════════════════════════

/**
 * Инициализация всех Pro-привязок.
 * Вызывать из main.js ПОСЛЕ initFeatureBindings и ПОСЛЕ viewport.js загружен.
 */
export function initProFeaturesBindings() {
  bindAIControls(); // [10.19.43]
  try {
    // 1. Заменяем заглушки реальными вызовами
    bindBookmarks();
    bindCine();
    // bindDICOMSC(); // [10.19.32] удалён вместе с кнопкой-сиротой

    // 2. Привязываем кнопки (уже в index.html) к viewport.js
    bindProButtons();

    // 3. Подписываемся на события для обновления UI
    bindEventListeners();

    // 4. Enterprise DatasetManager + MemoryGuardian UI
    bindDatasetManager();

    // 5. AI Module — все алгоритмы (lazy load при первом нажатии)
    bindAI();

    log.debug('[ProFeatures] All viewport.js + DatasetManager + AI wired to UI');
  } catch (e) {
    log.warn('[ProFeatures] Init error:', e.message);
  }
}

// ══════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — AI config, batch export, overlay, scheduling
// ══════════════════════════════════════════════════════════════

let _aiConfidence = 0.5; // default threshold
let _aiResultsVisible = true;
const _aiResultHistory = [];
const AI_HISTORY_MAX = 30;

/**
 * Set AI confidence threshold (filters results below this value)
 * @param {number} threshold — 0.0 to 1.0
 */
export function setAIConfidence(threshold) {
  _aiConfidence = Math.max(0, Math.min(1, threshold));
  showToast(`🤖 AI порог: ${(_aiConfidence * 100).toFixed(0)}%`, 'info', 1200);
}

/**
 * Get current AI confidence threshold
 * @returns {number}
 */
export function getAIConfidence() { return _aiConfidence; }

/**
 * Toggle AI result overlays visibility on viewports
 * @returns {boolean} new state
 */
export function toggleAIOverlays() {
  _aiResultsVisible = !_aiResultsVisible;
  document.querySelectorAll('.ai-overlay, .ai-segmentation-mask, [data-ai-result]').forEach(el => {
    el.style.display = _aiResultsVisible ? '' : 'none';
  });
  showToast(_aiResultsVisible ? '👁 ИИ оверлеи ВКЛ' : '🚫 ИИ оверлеи ВЫКЛ', 'info', 1000);
  return _aiResultsVisible;
}

/**
 * Run multiple AI analyses and export combined report
 * @param {string[]} analyses — e.g. ['calcium', 'cardiac', 'lung-nodules']
 * @returns {Promise<Object>}
 */
export async function runAIBatchAndExport(analyses) {
  const vol = _getActiveVolumeData();
  if (!vol) { showToast('⚠️ Нет активного тома', 'warning'); return null; }
  const ai = await _ai();
  const results = {};
  for (const name of analyses) {
    try {
      showToast(`⏳ AI: ${name}...`, 'info', 1500);
      switch (name) {
        case 'calcium': results[name] = await ai.calciumScoring(vol.scalarData, ...vol.dims, vol.spacing); break;
        case 'cardiac': results[name] = await ai.segmentCardiacChambers(vol.scalarData, ...vol.dims, vol.spacing); break;
        case 'organs': results[name] = await ai.segmentOrgansByHU(vol.scalarData, ...vol.dims, vol.spacing); break;
        case 'brain': results[name] = await ai.segmentBrainByHU(vol.scalarData, ...vol.dims, vol.spacing); break;
        case 'lung-nodules': results[name] = await ai.detectLungNodules(vol.scalarData, ...vol.dims, vol.spacing); break;
        default: results[name] = { error: 'Unknown analysis' };
      }
      _aiResultHistory.push({ name, timestamp: Date.now(), success: true });
    } catch (e) {
      results[name] = { error: e.message };
      _aiResultHistory.push({ name, timestamp: Date.now(), success: false, error: e.message });
    }
  }
  if (_aiResultHistory.length > AI_HISTORY_MAX) _aiResultHistory.splice(0, _aiResultHistory.length - AI_HISTORY_MAX);
  // Auto-export
  const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `ai_batch_${Date.now()}.json`; a.click();
  URL.revokeObjectURL(url);
  showToast(`🤖 Батч: ${analyses.length} анализов → JSON`, 'success', 2000);
  return results;
}

/**
 * Get AI analysis history
 * @returns {Array}
 */
export function getAIHistory() { return [..._aiResultHistory]; }

/**
 * Schedule AI analysis to run after volume load
 * @param {string[]} analyses — auto-run these after next volume load
 */
let _scheduledAI = null;
export function scheduleAIOnLoad(analyses) {
  _scheduledAI = analyses;
  showToast(`📋 ${analyses.length} AI анализов запланировано`, 'info', 1500);
}

export function getProFeaturesState() {
  return { activeDatasetId: _activeDatasetId, aiLoaded: !!_aiModule, confidence: _aiConfidence,
    overlaysVisible: _aiResultsVisible, historySize: _aiResultHistory.length, scheduled: _scheduledAI?.length || 0 };
}
export function getActiveVolumeInfo() { const v = _getActiveVolumeData(); return v ? { dims: v.dims, spacing: v.spacing, voxels: v.scalarData?.length || 0 } : null; }
export function getActiveDatasetId() { return _activeDatasetId; }
export function getMemoryInfo() { const m = performance.memory; return m ? { usedMB: +(m.usedJSHeapSize/1048576).toFixed(0), limitMB: +(m.jsHeapSizeLimit/1048576).toFixed(0) } : null; }
export async function preloadAIModule() { try { await (await _ai()).warmupModels?.(); showToast('🤖 AI готов', 'success'); return true; } catch { return false; } }
export async function releaseAIModels() { try { await (await _ai()).releaseModels?.(); showToast('🧹 AI выгружен', 'success'); } catch (e) { log.debug("[proFeaturesBindings]", e.message); } }
export function destroyActiveDataset() {
  _ac.abort(); _ac = new AbortController(); if (_activeDatasetId) { datasetManager.destroyDataset(_activeDatasetId); _activeDatasetId = null; } }
export function clearAllDatasets() { datasetManager.clearAll(); _activeDatasetId = null; }


// [10.19.43] Управление ИИ — переезд из Enterprise (решение владельца, п.9):
// порог ползунком вместо кнопок-хардкодов, прогрев и показ масок — рядом с
// картами моделей, где им и место. Функции — этого же модуля.
export function bindAIControls() {
  const п = document.getElementById('btn-ai-preload');
  п?.addEventListener('click', async () => { showToast('📥 Прогрев моделей…', 'info', 1500); try { await preloadAIModule(); showToast('✅ Модели готовы', 'success', 1500); } catch (e) { showToast('❌ ' + e.message, 'error', 2500); } });
  const пз = document.getElementById('ai-confidence');
  const пв = document.getElementById('ai-confidence-val');
  пз?.addEventListener('input', () => { const v = +пз.value; if (пв) пв.textContent = v + '%'; try { setAIConfidence(v / 100); } catch (e) { /* порог применится при запуске */ } });
  const т = document.getElementById('ai-show-overlays');
  // [11.1.35] Метка хозяина: без неё общий перещёлкиватель bindToggles щёлкал
  // класс вторым разом — вид «замерзал» либо расходился с действием.
  if (т) т.dataset.хозяин = 'proFeaturesBindings/ПоказМасок';
  т?.addEventListener('click', () => { const вкл = !т.classList.contains('on'); т.classList.toggle('on', вкл); т.setAttribute('aria-checked', String(вкл)); try { toggleAIOverlays(); } catch (e) { showToast('Маски: ' + e.message, 'info', 1500); } });
}
